/**
 * `Button` (W1-12 ui-kit-i18n; contract ButtonProps, DESIGN.md §8.1).
 *
 *  - Never takes focus on pointer down (unless a text field is focused, so it blurs and commits first); Space and
 *    Enter therefore keep reaching the global bindings after a click.
 *  - `command`: runs through the UiBridge on click unless the caller gave `onClick` (a handler merged in by a
 *    Radix trigger such as Popover does not count, see internal/commandButton.ts), shows its shortcut in a
 *    tooltip and is disabled with the registry's reason while the command is unavailable.
 *  - Disabled = `aria-disabled` (still focusable and hoverable), clicks are ignored and the reason is shown in
 *    the tooltip and announced through aria-describedby.
 *  - Wrapped in `<Tooltip>`, it shows that content in its own single tooltip (with the shortcut and reason).
 */

import { useId, type MouseEvent as ReactMouseEvent } from 'react';
import { commandDisabledReason, firstChord, useTr, useUiBridge } from './bridge';
import s from './Button.module.css';
import { Icon } from './Icon';
import type { NativeButtonProps } from './internal/buttonProps';
import { registerCommandButton, runsCommandOnClick, type CommandButtonPrivateProps } from './internal/commandButton';
import { cx } from './internal/cx';
import { keepFocusOnPointerDown } from './internal/focus';
import { Tooltip } from './Tooltip';
import type { ButtonProps } from './types';

export type ButtonAllProps = Omit<ButtonProps, 'onClick'> &
  NativeButtonProps<ButtonProps & CommandButtonPrivateProps> &
  CommandButtonPrivateProps & {
    /** Compact height for dense rows (default 'md'). */
    readonly size?: 'sm' | 'md';
  };

export function Button(props: ButtonAllProps) {
  const {
    children,
    variant = 'secondary',
    icon,
    command,
    disabledReason,
    type = 'button',
    autoFocus,
    helpId,
    size = 'md',
    onClick,
    callerOnClick,
    tooltip,
    onMouseDown,
    className,
    ...rest
  } = props;
  const bridge = useUiBridge();
  const tr = useTr();
  const reasonId = useId();
  const reason = disabledReason ?? commandDisabledReason(bridge, command);
  const disabled = reason !== null;
  const tooltipCommand = tooltip?.command ?? command;
  const chord = firstChord(bridge, tooltipCommand);

  const handleClick = (e: ReactMouseEvent<HTMLButtonElement>): void => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
    if (runsCommandOnClick(command, onClick, callerOnClick)) bridge.run(command);
  };
  const handleMouseDown = (e: ReactMouseEvent<HTMLButtonElement>): void => {
    onMouseDown?.(e);
    keepFocusOnPointerDown(e);
  };

  const describedBy = [rest['aria-describedby'], disabled ? reasonId : undefined].filter(Boolean).join(' ');
  const button = (
    <button
      {...rest}
      type={type}
      className={cx(s.button, s[variant], size === 'sm' && s.sm, disabled && s.disabled, className)}
      aria-disabled={disabled ? true : undefined}
      aria-describedby={describedBy === '' ? undefined : describedBy}
      data-help-id={helpId}
      data-variant={variant}
      autoFocus={autoFocus}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
    >
      {icon !== undefined && <Icon name={icon} size={size === 'sm' ? 14 : 16} />}
      <span className={s.label}>{children}</span>
    </button>
  );

  const reasonText = reason === null ? null : tr(reason);
  return (
    <>
      {tooltip !== undefined || chord !== undefined || reasonText !== null ? (
        <Tooltip
          content={tooltip === undefined ? children : tooltip.content}
          command={tooltipCommand}
          detail={reasonText ?? tooltip?.detail}
          side={tooltip?.side}
          open={tooltip?.open}
          onOpenChange={tooltip?.onOpenChange}
        >
          {button}
        </Tooltip>
      ) : (
        button
      )}
      {reasonText !== null && (
        <span id={reasonId} hidden>
          {reasonText}
        </span>
      )}
    </>
  );
}

registerCommandButton(Button);
